import * as THREE from 'three';
import store from '../../redux/store';
import { sceneStateHelper } from '../ThreeJsUtils/SceneStateHelper';
import { syncSceneVisibilityFromStore } from '../ThreeJsUtils/assetVisibilityUtils';
import { resetAnimationsToOriginalState } from '../ThreeJsUtils/applyAnimations';
import {
  stopAllSceneAudio,
  playPreviewAudioOnAwake,
  resumeAudioContext,
} from '../audioPlaybackUtils';
import { replayPreviewPhysics } from '../Physics/PhysicsUtils';
import { setHotspotBillboardEnabled } from '../../components/gridpagefunction/HotSpot';
import { resumeSceneParticleAnimations } from '../particles/particleAnimationUtils';
import { syncPreviewAssetEvents } from '../eventAssetUtils';

function replayPreviewVideos(scene) {
  const sceneVideos = store.getState().assets_object?.sceneVideos || [];

  // Build a unified list: Redux sceneVideos PLUS any video meshes already in the Three.js
  // scene that haven't been registered in Redux yet (timing race on initial published load).
  const seenMeshes = new Set();
  const allVideoMeshes = [];

  sceneVideos.forEach((mesh) => {
    if (mesh && !seenMeshes.has(mesh)) {
      seenMeshes.add(mesh);
      allVideoMeshes.push(mesh);
    }
  });

  if (scene) {
    scene.traverse((obj) => {
      if (obj.deleteType === 'video' && !seenMeshes.has(obj)) {
        seenMeshes.add(obj);
        allVideoMeshes.push(obj);
      }
    });
  }

  const seenVideos = new Set();

  allVideoMeshes.forEach((mesh, idx) => {
    if (!mesh) return;

    // Resolve the HTMLVideoElement — check direct ref, standard VideoTexture map,
    // AND chroma key ShaderMaterial uniform (tex) which holds the VideoTexture.
    let video = mesh.videoElement || null;
    if (!video) {
      const mat = mesh.material;
      if (mat) {
        if (mat.map instanceof THREE.VideoTexture) {
          video = mat.map.image || null;
        } else if (mat.isShaderMaterial && mat.uniforms?.tex?.value instanceof THREE.VideoTexture) {
          video = mat.uniforms.tex.value.image || null;
        }
      }
    }

    if (!video || typeof video.play !== 'function' || seenVideos.has(video)) {
      return;
    }
    seenVideos.add(video);

    const settings = mesh.userData?.video_settings || {};

    const url =
      mesh.asset_file_path || mesh.userData?.video_settings?.video_clip;

    // Always sync properties (loop, mute, volume, speed) regardless of play state
    video.loop = settings.loop ?? false;
    video.muted = settings.mute ?? false;
    video.playbackRate = settings.playback_speed ?? 1;
    video.volume = settings.volume ?? 0.5;

    // ── DOUBLE-AUDIO GUARD ─────────────────────────────────────────────────────
    // If DownloadVideo's async .then() already started this video (it resolved
    // before replayPreviewMode ran), do NOT pause + re-play — that produces a
    // second concurrent audio context on looped videos in some browsers.
    // Instead, just sync the properties above and attach the ended-handler if needed.
    const alreadyPlaying = !video.paused && !video.ended;

    const playWhenReady = () => {
      const isAutoPlay =
        settings.play_on_awake === true ||
        String(settings.play_on_awake) === 'true';
      // playback_state takes precedence: if set to Pause or Stop, never autoplay
      const playbackState = settings.playback_state;
      const isBlockedByPlaybackState =
        playbackState === 'Pause' || playbackState === 'Stop';
      const isLooping = settings.loop ?? false;

      if (isAutoPlay && !isBlockedByPlaybackState) {
        // For chroma key ShaderMaterial, Three.js does NOT auto-tick needsUpdate
        // on shader uniforms — only on material.map. Start a rAF loop to keep
        // the tex uniform's VideoTexture updating each frame.
        if (
          mesh.material?.isShaderMaterial &&
          mesh.material?.uniforms?.tex?.value instanceof THREE.VideoTexture
        ) {
          const chromaTex = mesh.material.uniforms.tex.value;
          // Avoid stacking multiple loops on the same mesh
          if (!mesh._chromaUpdateLoop) {
            const tick = () => {
              if (!mesh._chromaUpdateLoop) return; // stopped
              chromaTex.needsUpdate = true;
              mesh._chromaUpdateLoop = requestAnimationFrame(tick);
            };
            mesh._chromaUpdateLoop = requestAnimationFrame(tick);
          }
        }
        // once playback finishes.
        if (!isLooping) {
          if (video._replayEndedHandler) {
            video.removeEventListener('ended', video._replayEndedHandler);
          }
          video._replayEndedHandler = () => {
            video.pause();
            video.currentTime = 0;
            const previousVolume = video.volume;
            video.volume = 0;
            requestAnimationFrame(() => {
              video.volume = previousVolume;
            });
          };
          video.addEventListener('ended', video._replayEndedHandler, { once: true });
        }

        // ── DOUBLE-AUDIO GUARD ─────────────────────────────────────────────
        // Use _playStarted flag to coordinate with DownloadVideo's async .then().
        // Whoever sets the flag first owns playback; the other side skips play().
        if (video._playStarted || (!video.paused && !video.ended)) {
          // already claimed/playing — skip
        } else {
          video._playStarted = true; // claim playback ownership
          video.play().catch((err) => {
            video._playStarted = false;
            console.warn(`Video play() rejected for "${mesh.name}":`, err);
          });
        }
      } else {
        // Blocked — ensure we stop the video if it was somehow started
        if (!video.paused) video.pause();
        video._playStarted = false;
        // Cancel chroma texture update loop if running
        if (mesh._chromaUpdateLoop) {
          cancelAnimationFrame(mesh._chromaUpdateLoop);
          mesh._chromaUpdateLoop = null;
        }
        if (playbackState === 'Stop') {
          video.currentTime = 0;
        }
      }
    };

    if (url && video.src !== url) {
      // src mismatch — must reload, reset flag and pause first
      video._playStarted = false;
      video.pause();
      video.currentTime = 0;
      video.src = url;
      video.load();
      video.addEventListener('canplay', playWhenReady, { once: true });
    } else if (alreadyPlaying) {
      // Video is already running with correct src — sync properties and attach ended-handler
      playWhenReady();
    } else if (video.readyState >= 1) {
      // Video has metadata, paused, correct src — reset flag and start fresh
      video._playStarted = false;
      video.pause();
      video.currentTime = 0;
      playWhenReady();
    } else {
      // Src set but not yet decoded — reset flag and wait
      video._playStarted = false;
      video.pause();
      video.currentTime = 0;
      video.addEventListener('canplay', playWhenReady, { once: true });
    }
  });
}

function refreshHotspotBillboards() {
  const hotspotList = store.getState().assets_object?.sceneHotspots || [];
  hotspotList.forEach((hp) => {
    try {
      setHotspotBillboardEnabled(hp, true);
    } catch (_) {}
  });
}

/**
 * Restart the full preview experience (transforms, animations, physics, media).
 * Used by Replay and when entering preview mode.
 */
export function replayPreviewMode({
  scene,
  stopAllAnimations,
  startAllAnimations,
  renderer,
  camera,
} = {}) {
  if (!scene) return;

  if (sceneStateHelper.hasSavedStates()) {
    sceneStateHelper.restoreStates(scene);
  }

  stopAllSceneAudio(scene);
  resumeAudioContext();

  if (typeof stopAllAnimations === 'function') {
    stopAllAnimations();
  }

  const animationObjects =
    store.getState().assets_object?.animationObjects || [];
  if (animationObjects.length) {
    resetAnimationsToOriginalState(scene, animationObjects, {
      restoreTransforms: false,
      restoreMaterials: true,
    });
  }

  replayPreviewPhysics(scene);
  syncPreviewAssetEvents(scene);
  syncSceneVisibilityFromStore(scene);
  replayPreviewVideos(scene);
  playPreviewAudioOnAwake(scene);

  if (typeof startAllAnimations === 'function') {
    startAllAnimations();
  }

  resumeSceneParticleAnimations(scene);

  refreshHotspotBillboards();

  if (renderer && camera) {
    renderer.render(scene, camera);
  }
}
